// ----------------------------------------------------------------------------
// acid-wav-parser — parse/write ACIDized WAV metadata from RIFF `acid ` chunk
//
// The `acid ` chunk (note trailing space in chunk ID) contains:
//   - type:     uint32le  (0=loop, 1=one-shot, 2=beatmapped)
//   - rootNote: float32le (MIDI note number, e.g. 60 = C3)
//   - unknown1: float32le (usually 0)
//   - tempo:    float32le (original BPM)
//   - unknown2: uint32le  (usually 0)
//   - beats:    uint32le  (number of beats in loop)
//   - meterDen: uint32le  (meter denominator, e.g. 4 for 4/4)
//   - unknown3: uint32le  (usually 0)
//
// Reference: ACID Pro documentation + SoundForge ACID chunk spec
// ----------------------------------------------------------------------------

const ACID_CHUNK_ID = 'acid '; // 5 bytes incl trailing space

function readUint32LE(data, offset) {
   return (data[offset] | (data[offset+1] << 8) | (data[offset+2] << 16) | (data[offset+3] << 24)) >>> 0;
}

function readFloat32LE(data, offset) {
   const buf = new ArrayBuffer(4);
   const view = new DataView(buf);
   for (let i = 0; i < 4; i++) view.setUint8(i, data[offset + i]);
   return view.getFloat32(0, true);
}

function writeUint32LE(arr, offset, val) {
   arr[offset] = val & 0xff;
   arr[offset+1] = (val >> 8) & 0xff;
   arr[offset+2] = (val >> 16) & 0xff;
   arr[offset+3] = (val >> 24) & 0xff;
}

function writeFloat32LE(arr, offset, val) {
   const buf = new ArrayBuffer(4);
   const view = new DataView(buf);
   view.setFloat32(0, val, true);
   for (let i = 0; i < 4; i++) arr[offset + i] = view.getUint8(i);
}

function readString(data, offset, len) {
   let s = '';
   for (let i = 0; i < len; i++) s += String.fromCharCode(data[offset + i]);
   return s;
}

/**
 * Parse ACID metadata from a WAV ArrayBuffer.
 * Returns { type, rootNote, tempo, beats, meterDen } or null if no `acid ` chunk.
 */
function parseAcidChunk(wavBuffer) {
   const data = wavBuffer instanceof Uint8Array ? wavBuffer : new Uint8Array(wavBuffer);
   if (data.length < 12) return null;
   if (readString(data, 0, 4) !== 'RIFF') return null;
   if (readString(data, 8, 4) !== 'WAVE') return null;

   let offset = 12;
   while (offset + 8 <= data.length) {
      const chunkId = readString(data, offset, 4);
      const chunkSize = readUint32LE(data, offset + 4);
      offset += 8;

      if (chunkId === ACID_CHUNK_ID.slice(0, 4)) {
         // Check for the trailing space variant (some files use "acid" without space)
         if (chunkSize >= 24) {
            return {
               type: readUint32LE(data, offset),
               rootNote: readFloat32LE(data, offset + 4),
               unknown1: readFloat32LE(data, offset + 8),
               tempo: readFloat32LE(data, offset + 12),
               unknown2: readUint32LE(data, offset + 16),
               beats: readUint32LE(data, offset + 20),
               meterDen: readUint32LE(data, offset + 24),
            };
         }
      }

      // Chunks are word-aligned (padded to even size)
      offset += chunkSize + (chunkSize % 2);
   }
   return null;
}

/**
 * Build an `acid ` chunk to embed in a WAV file.
 * Returns Uint8Array of the chunk (8-byte header + 28-byte payload).
 */
function buildAcidChunk(meta) {
   const payload = new Uint8Array(28);
   writeUint32LE(payload, 0, meta.type || 0);
   writeFloat32LE(payload, 4, meta.rootNote || 60);
   writeFloat32LE(payload, 8, 0); // unknown1
   writeFloat32LE(payload, 12, meta.tempo || 120);
   writeUint32LE(payload, 16, 0); // unknown2
   writeUint32LE(payload, 20, meta.beats || 4);
   writeUint32LE(payload, 24, meta.meterDen || 4);
   return payload;
}

const ACID_TYPE_NAMES = {
   0: 'loop',
   1: 'oneshot',
   2: 'beatmapped',
};

const ACID_TYPE_VALUES = {
   loop: 0,
   oneshot: 1,
   beatmapped: 2,
};

module.exports = {
   parseAcidChunk,
   buildAcidChunk,
   ACID_TYPE_NAMES,
   ACID_TYPE_VALUES,
};
